// perfect-freehand のラッパー。
// stage 座標の入力点列から SVG path 文字列を生成する。
// 入力中はクライアント側のみで描画し、確定時にこの関数で path を計算して保存する。

import { getStroke } from "perfect-freehand";

export interface StrokePoint {
  readonly x: number;
  readonly y: number;
  readonly pressure: number;
}

interface RenderOptions {
  readonly thickness: number;
  readonly simulatePressure?: boolean | undefined;
}

/**
 * 入力点列から SVG path 文字列を生成する。
 * thickness はピクセル単位（PEN_THICKNESS_PIXELS の値を渡す想定）。
 */
export function buildStrokePath(points: readonly StrokePoint[], options: RenderOptions): string {
  if (points.length === 0) return "";
  const stroke = getStroke(
    points.map((p) => [p.x, p.y, p.pressure]),
    {
      size: options.thickness,
      thinning: 0.5,
      smoothing: 0.5,
      streamline: 0.5,
      simulatePressure: options.simulatePressure ?? true,
      last: true,
    }
  );
  if (stroke.length === 0) return "";
  return svgPathFromOutline(stroke);
}

/** アウトライン算出に必要な最小の形。共有メモのキーにもこの参照をそのまま使う。 */
interface SharedStrokeSource {
  readonly points: readonly StrokePoint[];
  readonly thickness: number;
}

/** アウトラインの閉多角形の頂点（stage 座標。path と同じく小数点以下 2 桁へ丸めた値）。 */
export interface StrokeOutlineVertex {
  readonly x: number;
  readonly y: number;
}

/** 共有メモの 1 件。頂点は使う箇所（他参加者のバッジの接点）が限られるため、求められたときにだけ作る。 */
interface SharedStrokeOutline {
  readonly path: string;
  vertices: readonly StrokeOutlineVertex[] | null;
}

// 同じペンストロークのアウトラインを、描画する箇所（オブジェクト本体・自分の選択輪郭・
// 他参加者の選択輪郭）の間で共有する。useMemo はコンポーネントのインスタンス内に閉じるため、
// 同じストロークを複数の参加者が選択すると getStroke がインスタンスの数だけ走ってしまう。
// doc store のオブジェクトは更新時に必ず新しい参照へ置き換わるため、参照をキーにすれば
// 内容の変化を取りこぼさない。WeakMap なので、置き換えられた古いオブジェクトのエントリは
// GC に任せられる（明示的な無効化が要らない）。
const strokeOutlineCache = new WeakMap<SharedStrokeSource, SharedStrokeOutline>();

function getSharedStrokeOutlineEntry(object: SharedStrokeSource): SharedStrokeOutline {
  const cached = strokeOutlineCache.get(object);
  if (cached !== undefined) return cached;
  const entry: SharedStrokeOutline = {
    path: buildStrokePath(object.points, { thickness: object.thickness }),
    vertices: null,
  };
  strokeOutlineCache.set(object, entry);
  return entry;
}

/**
 * 同一オブジェクトのアウトラインを 1 回だけ算出し、以降は同じ path 文字列を返す。
 * 算出結果は buildStrokePath と同一で、キャッシュの有無で描画は変わらない。
 */
export function getSharedStrokePath(object: SharedStrokeSource): string {
  return getSharedStrokeOutlineEntry(object).path;
}

/**
 * getSharedStrokePath と同じ算出結果（同じ共有メモ）の閉多角形の頂点を返す。
 * 頂点は path に書かれた座標そのもの（小数点以下 2 桁）で、描画される輪郭と食い違わない。
 * perfect-freehand を再計算せず、最初に求められたときに path から 1 回だけ取り出して共有メモへ残す。
 * アウトラインが空（points が 0 件など）のときは空配列。
 */
export function getSharedStrokeOutlineVertices(
  object: SharedStrokeSource
): readonly StrokeOutlineVertex[] {
  const entry = getSharedStrokeOutlineEntry(object);
  if (entry.vertices === null) entry.vertices = parseStrokePathVertices(entry.path);
  return entry.vertices;
}

/**
 * buildStrokePath が作る path（`M x,y L x,y ... Z`）から頂点を取り出す。
 * 数値として読めない座標（入力に非有限値が混じった場合など）は頂点に含めない。
 */
export function parseStrokePathVertices(path: string): readonly StrokeOutlineVertex[] {
  const vertices: StrokeOutlineVertex[] = [];
  for (const match of path.matchAll(/[ML]([^,\s]+),([^\s]+)/g)) {
    const x = Number(match[1]);
    const y = Number(match[2]);
    if (Number.isFinite(x) && Number.isFinite(y)) vertices.push({ x, y });
  }
  return vertices;
}

/**
 * Canvas2D 上で確定済みストロークを fill する。
 * stroke は perfect-freehand が返したアウトライン（[x,y][]）。
 */
export function fillStrokeOnCanvas(
  ctx: CanvasRenderingContext2D,
  points: readonly StrokePoint[],
  options: RenderOptions & { readonly color: string }
): void {
  if (points.length === 0) return;
  const outline = getStroke(
    points.map((p) => [p.x, p.y, p.pressure]),
    {
      size: options.thickness,
      thinning: 0.5,
      smoothing: 0.5,
      streamline: 0.5,
      simulatePressure: options.simulatePressure ?? true,
      last: true,
    }
  );
  if (outline.length === 0) return;
  ctx.beginPath();
  const first = outline[0];
  if (!first) return;
  ctx.moveTo(first[0], first[1]);
  for (let i = 1; i < outline.length; i += 1) {
    const point = outline[i];
    if (!point) continue;
    ctx.lineTo(point[0], point[1]);
  }
  ctx.closePath();
  ctx.fillStyle = options.color;
  ctx.fill();
}

function svgPathFromOutline(outline: readonly number[][]): string {
  if (outline.length === 0) return "";
  let path = "";
  for (let i = 0; i < outline.length; i += 1) {
    const point = outline[i];
    if (!point) continue;
    const [x, y] = point;
    if (typeof x !== "number" || typeof y !== "number") continue;
    path += i === 0 ? `M${x.toFixed(2)},${y.toFixed(2)}` : ` L${x.toFixed(2)},${y.toFixed(2)}`;
  }
  return `${path} Z`;
}
